import { useState } from "react";

interface TabItem {
  id: string;
  label: string;
  content: React.ReactNode;
}

const tabs: TabItem[] = [
  {
    id: "about",
    label: "About",
    content: (
      <>
        <p className="mb-4">
          Southern Transit is Trinidad, Colorado's public transportation system, providing reliable and accessible transit for the community.
        </p>
        <p className="mb-2">We offer:</p>
        <ul className="list-disc list-inside space-y-1 text-foreground-700 mb-4">
          <li>Fixed-route bus services for everyday travel throughout Trinidad and surrounding areas.</li>
          <li>ADA Paratransit services for riders with disabilities.</li>
          <li>Community Health Transportation (CHT) for medical appointments in the region.</li>
          <li>Vanpools for commuters traveling to and from work in neighboring communities. Please email lynn@southerntransit.us for pricing and details.</li>
        </ul>
        <p>
          At Southern Transit, we are committed to service excellence, community engagement, integrity, environmental responsibility, and economic sustainability—all to keep our community moving, affordably and efficiently.
        </p>
      </>
    ),
  },
  {
    id: "service-excellence",
    label: "Service Excellence",
    content: (
      <ul className="list-disc list-inside space-y-2 text-foreground-700">
        <li>Ensure a safe & comfortable experience for both riders and employees.</li>
        <li>Increase service frequency to meet community needs better.</li>
        <li>Maintain our commitment to reliable, accessible services for all riders.</li>
        <li>Serve as community ambassadors, promoting accessibility and sustainability.</li>
      </ul>
    ),
  },
  {
    id: "community-engagement",
    label: "Community Engagement",
    content: (
      <ul className="list-disc list-inside space-y-2 text-foreground-700">
        <li>Encourage people to become Southern Transit riders and advocate for our services.</li>
        <li>Maintain strong, collaborative relationships with community partners.</li>
        <li>Earn "safe for kids" status, ensuring a trusted and secure environment for all ages.</li>
        <li>Expand our reach and engagement across diverse communities and groups.</li>
      </ul>
    ),
  },
  {
    id: "organizational-integrity",
    label: "Organizational Integrity",
    content: (
      <ul className="list-disc list-inside space-y-2 text-foreground-700">
        <li>Collaborate, support, & respect everyone, always.</li>
        <li>Maintain highest standards for ethics, integrity, & transparency.</li>
        <li>Recruit, train, reward, & retain a diverse, high-quality workforce committed to excellence.</li>
      </ul>
    ),
  },
  {
    id: "environmental",
    label: "Environmental Commitment",
    content: (
      <ul className="list-disc list-inside space-y-2 text-foreground-700">
        <li>Operate a clean, efficient fleet for a greener, cleaner future.</li>
        <li>Build & operate facilities that follow sustainability best practices.</li>
        <li>Promote eco-friendly mobility: walk, bike, bus, and vanpool.</li>
      </ul>
    ),
  },
  {
    id: "economic",
    label: "Economic Sustainability",
    content: (
      <ul className="list-disc list-inside space-y-2 text-foreground-700">
        <li>Offer fair compensation, including wages and essential benefits.</li>
        <li>Diversify funding sources to ensure financial stability.</li>
        <li>Seek revenue opportunities that complement federal and other grant funding.</li>
        <li>Preserve budget integrity and financial performance.</li>
      </ul>
    ),
  },
  {
    id: "free-routes",
    label: "Our Routes",
    content: (
      <>
        <p className="mb-2">
          • Year-round and peak-season routes.{" "}
          <a href="/airport-shuttle" className="text-primary-600 hover:underline">Click Here</a> to view our schedules.
        </p>
        <p className="mb-2">• Deviated fixed route service available for Trinidad</p>
        <p>• Main Route connects Trinidad and surrounding communities</p>
      </>
    ),
  },
  {
    id: "other-transportation",
    label: "Other Transportation",
    content: (
      <>
        <p className="mb-2">
          • Vanpools for surrounding communities.{" "}
          Please email lynn@southerntransit.us to learn more.
        </p>
        <p className="mb-2">
          • Paratransit ADA service within the Trinidad area.{" "}
          Call (712) 206-1196 to learn more.
        </p>
        <p>
          • CHT (Community Health Transportation) to regional medical centers.{" "}
          Call (712) 206-1196 to learn more.
        </p>
      </>
    ),
  },
];

export default function AboutTabs() {
  const [activeTab, setActiveTab] = useState("about");

  const activeContent = tabs.find((t) => t.id === activeTab)?.content;

  return (
    <section className="w-full bg-background-50">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-10 md:py-16">
        {/* Heading */}
        <div className="text-center mb-10 md:mb-14">
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-foreground-900">
            About{" "}
            <em className="text-primary-500 font-medium not-italic">Southern Transit</em>
          </h2>
        </div>

        {/* Desktop: Vertical Tabs */}
        <div className="hidden md:flex gap-8 lg:gap-12">
          {/* Tab List */}
          <nav className="w-[260px] shrink-0 border-l-2 border-background-200/70">
            <ul className="space-y-0">
              {tabs.map((tab) => {
                const isActive = tab.id === activeTab;
                return (
                  <li key={tab.id}>
                    <button
                      onClick={() => setActiveTab(tab.id)}
                      className={`block w-full text-left px-5 py-3 text-base font-heading italic transition-colors ${
                        isActive
                          ? "text-primary-600 border-l-2 border-primary-500 -ml-[2px]"
                          : "text-foreground-600 hover:text-primary-600"
                      }`}
                    >
                      {tab.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Content Panel */}
          <div className="flex-1 min-w-0">
            <div className="text-base md:text-lg text-foreground-700 leading-relaxed">
              {activeContent}
            </div>
          </div>
        </div>

        {/* Mobile: Accordion */}
        <div className="md:hidden space-y-2">
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <div key={tab.id} className="border border-background-200/70 rounded-lg overflow-hidden">
                <button
                  onClick={() => setActiveTab(isActive ? "" : tab.id)}
                  className={`w-full text-left px-5 py-3.5 font-heading italic text-base transition-colors flex items-center justify-between ${
                    isActive ? "text-primary-600 bg-primary-50/50" : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  {tab.label}
                  <span className="w-5 h-5 flex items-center justify-center">
                    {isActive ? (
                      <i className="ri-arrow-up-s-line text-lg" />
                    ) : (
                      <i className="ri-arrow-down-s-line text-lg" />
                    )}
                  </span>
                </button>
                {isActive && (
                  <div className="px-5 py-4 text-sm text-foreground-700 leading-relaxed border-t border-background-200/70">
                    {tab.content}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}